import { useAuth } from '@deltasuite/auth';
import {
  PROJECT_TYPES,
  PROJECT_TYPE_LABELS,
  type ProjectType,
  type ZohoContact,
} from '@deltasuite/zoho-client';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { getPool } from '../api/pool';
import { getToday, type TodayResponse } from '../api/today';
import { connectZoho } from '../oauth/connectZoho';
import { PointsRing } from './PointsRing';

interface Props {
  onStart: (contacts: readonly ZohoContact[]) => void;
  onBuildList: () => void;
}

// The dial cockpit. Replaces the filter builder as the front door per the
// reframe brief. Massive points-target ring + project-type chips (default
// all four ON) + one big "Start dialling" button. Filter builder lives
// behind the small "Build a call list" link for the rare case a rep wants
// a non-default audience.
export function HomeScreen({ onStart, onBuildList }: Props) {
  const { getAccessToken } = useAuth();
  const [today, setToday] = useState<TodayResponse | null>(null);
  const [selected, setSelected] = useState<ProjectType[]>([...PROJECT_TYPES]);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onConnectZoho = useCallback(() => {
    void connectZoho(getAccessToken).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Unknown error.';
      setError(`Couldn't start Zoho connect: ${msg}`);
    });
  }, [getAccessToken]);

  useEffect(() => {
    void getToday()
      .then(setToday)
      .catch(() => {
        // Quiet — the ring shows 0/30 default if the call fails.
        setToday({ points: 0, target: 30, day_start: '' });
      });
  }, []);

  const toggle = useCallback((t: ProjectType) => {
    setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  }, []);

  const start = async () => {
    if (selected.length === 0) {
      setError('Pick at least one project type.');
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const pool = await getPool(selected, 50);
      if (pool.data.length === 0) {
        setError(
          'No contacts available — every match is in the 14-day company cooldown. Try a different project type.',
        );
        return;
      }
      onStart(pool.data);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Couldn't load the pool (${err.status}). Worker reachable?`);
      } else {
        setError('Could not load the contact pool.');
      }
    } finally {
      setStarting(false);
    }
  };

  const points = today?.points ?? 0;
  const target = today?.target ?? 30;

  return (
    <main className="flex min-h-full flex-col items-center justify-center px-6 py-12">
      <div className="flex w-full max-w-xl flex-col items-center gap-10">
        <PointsRing points={points} target={target} />

        <section className="w-full">
          <h2 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
            Project types
          </h2>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {PROJECT_TYPES.map((t) => {
              const active = selected.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggle(t)}
                  aria-pressed={active}
                  className={
                    'rounded-full border px-4 py-2 font-sans text-sm transition-colors ' +
                    (active
                      ? 'border-signal-amber bg-signal-amber/10 text-signal-amber'
                      : 'border-graphite-300 bg-surface text-graphite-700 hover:bg-surface-sunk')
                  }
                >
                  {PROJECT_TYPE_LABELS[t]}
                </button>
              );
            })}
          </div>
        </section>

        <button
          type="button"
          onClick={start}
          disabled={starting}
          className="w-full rounded-lg bg-signal-amber px-8 py-5 font-sans text-xl font-semibold text-surface transition-colors hover:bg-signal-amber-hover disabled:opacity-50"
        >
          {starting ? 'Loading pool…' : 'Start dialling'}
        </button>

        {error ? (
          <p role="alert" className="font-mono text-xs text-signal-amber">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={onBuildList}
            className="font-mono text-[0.7rem] uppercase tracking-wider text-graphite-500 hover:text-graphite-900"
          >
            Build a call list →
          </button>
          <button
            type="button"
            onClick={onConnectZoho}
            className="font-mono text-[0.7rem] uppercase tracking-wider text-graphite-500 hover:text-graphite-900"
          >
            Connect Zoho →
          </button>
        </div>
      </div>
    </main>
  );
}
